/**
 * UsageTimeCard
 *
 * ```tsx
 * <UsageTimeCard
 *   value={1234.5}
 *   changePercent={15.2}
 *   isPositiveChange={true}
 *   chartData={[
 *     { date: '2025-01-01', value: 100 },
 *     { date: '2025-01-02', value: 120 },
 *     // ...
 *   ]}
 * />
 * ```
 */

import { Area } from '@antv/g2plot'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useThemeMode } from '@/contexts/ThemeContext'
import { formatFloat } from '@/utils/formatter'
import { cn } from '@/utils/style'
import { getCssVarRgb } from '@/utils/theme-color'

// ==================== Types ====================
interface ChartDataPoint {
  date: string
  value: number
}

interface UsageTimeCardProps {
  title: string
  value: number
  isValuePercent?: boolean
  changePercent?: number
  isPositiveChange?: boolean
  chartData?: ChartDataPoint[]
  className?: string
}

function UsageChart({
  data,
  isPositiveChange,
  changePercent,
  isValuePercent = false,
}: {
  data: ChartDataPoint[]
  isPositiveChange?: boolean
  changePercent?: number
  isValuePercent?: boolean
}) {
  const { t } = useTranslation('privateRegion')
  const { themeMode } = useThemeMode()
  const containerRef = useRef<HTMLDivElement>(null)

  const getLineColor = () => {
    if (changePercent === undefined || changePercent === 0) {
      return getCssVarRgb('--color-neutral-400', '#9ca3af') // gray-400
    }
    if (isPositiveChange) return getCssVarRgb('--color-success', '#1FC16B') // green
    return getCssVarRgb('--color-error', '#FB3748') // red
  }

  useEffect(() => {
    if (!containerRef.current) return

    const lineColor = getLineColor()

    const area = new Area(containerRef.current, {
      data,
      xField: 'date',
      yField: 'value',
      width: 128,
      height: 70,
      color: lineColor,
      areaStyle: {
        fill: getCssVarRgb('--color-panel-white', '#FFFFFF'),
      },
      xAxis: false,
      yAxis: false,
      padding: [0, 0, 4, 0], // top, right, bottom, left
      line: {
        style: {
          strokeWidth: 2,
        },
      },
      tooltip: {
        domStyles: {
          'g2-tooltip': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            padding: 0,
            border: 'none',
          },
        },
        customContent: (_title: string, items: any[]) => {
          if (!items || items.length === 0) return ''
          const item = items[0]
          if (!item || !item.data) return ''

          const { date, value } = item.data

          const displayValue = isValuePercent
            ? formatFloat(value * 100)
            : formatFloat(value, 2, true)
          const unit = isValuePercent ? '%' : t('usageTime.hours')
          return `
            <div style="background-color: #000; color: #fff; padding: 8px 12px; border-radius: 8px; font-size: 14px; font-weight: 600; line-height: 20px; box-shadow: 0 2px 8px rgba(0,0,0,0.15); margin-top: -20px;">
              <div style="margin-bottom: 4px;">${date}</div>
              <div>${displayValue} ${unit}</div>
            </div>
          `
        },
      },
    })

    area.render()

    return () => {
      area.destroy()
    }
  }, [data, isPositiveChange, changePercent, isValuePercent, t, themeMode])

  return <div ref={containerRef} />
}

export function UsageTimeCard({
  title,
  value,
  isValuePercent = false,
  changePercent,
  isPositiveChange,
  chartData = [],
  className,
}: UsageTimeCardProps) {
  const { t } = useTranslation('privateRegion')
  const [displayValue, setDisplayValue] = useState<number>(value)
  const prevValueRef = useRef<number>(value)

  useEffect(() => {
    const target = Number.isNaN(value) ? 0 : value
    const start = Number.isNaN(prevValueRef.current) ? 0 : prevValueRef.current
    const duration = 800 // ms
    const startTs = performance.now()

    const step = (now: number) => {
      const progress = Math.min((now - startTs) / duration, 1)
      const current = start + (target - start) * progress
      setDisplayValue(current)

      if (progress < 1) requestAnimationFrame(step)
    }

    requestAnimationFrame(step)
    prevValueRef.current = target
  }, [value])

  // --- SVG Arrow Icon (inline) ---
  const ArrowIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn('w-6 h-6', !isPositiveChange && 'rotate-90')}
    >
      <path
        d="M7 17L17 7M17 7H7M17 7V17"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )

  return (
    <div className={cn('bg-panel-white rounded-2xl p-6 border border-neutral-200', className)}>
      <div className="flex justify-between items-center mb-4">
        <div className="t-sb">{title}</div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-y-2">
          <div className="h3">
            {isValuePercent
              ? `${formatFloat(displayValue * 100)} %`
              : formatFloat(displayValue, 2, true)}
          </div>
          {changePercent !== undefined && (
            <div className="flex items-center gap-x-2">
              <div
                className={cn(
                  't-bold flex items-center gap-x-0.5',
                  changePercent === 0
                    ? 'text-tertiary'
                    : isPositiveChange
                      ? 'text-green-500'
                      : 'text-red-500',
                )}
              >
                {changePercent !== 0 && <ArrowIcon />}
                {changePercent === 0 ? '-' : formatFloat(changePercent)}%
              </div>
              <div className="t-sm text-tertiary">{t('usageTime.vsLastMonth')}</div>
            </div>
          )}
        </div>
        {chartData.length > 0 && (
          <div className="self-end">
            <UsageChart
              data={chartData}
              isPositiveChange={isPositiveChange}
              changePercent={changePercent}
              isValuePercent={isValuePercent}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default UsageTimeCard
